import React, { Suspense } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { Loader2 } from 'lucide-react';

import { DashboardLayout } from './components/DashboardLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { GuestRoute } from './components/GuestRoute';
import { WindowControls } from './components/WindowControls';
import { KeyboardShortcutsProvider } from './components/KeyboardShortcutsProvider';
import { BackendHealthCheck } from './components/BackendHealthCheck';
import { ConfirmProvider } from './components/ConfirmProvider';
import { RoleRoute } from './components/RoleRoute';
import { useTokenRefresh } from './hooks/useTokenRefresh';
import { usePermissions } from './hooks/usePermissions';
import { PERMISSIONS, homePathFor } from './lib/permissions';

const ProductsPage = React.lazy(() => import('./pages/products/ProductsPage'));
const ProductCreationPage = React.lazy(() => import('./pages/products/ProductCreationPage'));
const ProductDetailPage = React.lazy(() => import('./pages/products/ProductDetailPage'));
const ProductEditPage = React.lazy(() => import('./pages/products/ProductEditPage'));
const ProductImportPage = React.lazy(() => import('./pages/products/ProductImportPage'));
const CategoriesPage = React.lazy(() => import('./pages/categories/CategoriesPage'));
const CustomersPage = React.lazy(() => import('./pages/customers/CustomersPage'));
const SuppliersPage = React.lazy(() => import('./pages/suppliers/SuppliersPage'));
const ExpensesPage = React.lazy(() => import('./pages/expenses/ExpensesPage'));
const PurchasesPage = React.lazy(() => import('./pages/purchases/PurchasesPage'));
const SalesPage = React.lazy(() => import('./pages/sales/SalesPage'));
const POSPage = React.lazy(() => import('./pages/sales/POSPage'));
const ProformasPage = React.lazy(() => import('./pages/proformas/ProformasPage'));
const SettingsPage = React.lazy(() => import('./pages/settings/SettingsPage'));
const UsersManagementPage = React.lazy(() => import('./pages/settings/UsersManagementPage'));
const BackupPage = React.lazy(() => import('./pages/settings/BackupPage'));
const BranchesPage = React.lazy(() => import('./pages/settings/BranchesPage'));
const FiscalConfigPage = React.lazy(() => import('./pages/configuration/FiscalConfigPage'));
const IncomesPage = React.lazy(() => import('./pages/incomes/IncomesPage'));
const InventoryPage = React.lazy(() => import('./pages/products/InventoryPage'));

const LoginPage = React.lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const CashRegisterPage = React.lazy(() => import('./pages/CashRegisterPage').then(m => ({ default: m.CashRegisterPage })));
const CustomerAccountsPage = React.lazy(() => import('./features/customer-accounts').then(m => ({ default: m.CustomerAccountsPage })));
const AccountStatementPage = React.lazy(() => import('./features/customer-accounts').then(m => ({ default: m.AccountStatementPage })));
const ReportsPage = React.lazy(() => import('./features/reports').then(m => ({ default: m.ReportsPage })));

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false } },
});

/** Pantalla solo para admin; el resto de los roles vuelve a su pantalla de inicio. */
const adminOnly = (element: React.ReactNode) => <RoleRoute access="admin">{element}</RoleRoute>;

/** Lleva a cada rol a su pantalla de inicio, esperando a que cargue el perfil. */
function HomeRedirect() {
  const { role, isLoaded } = usePermissions();
  if (!isLoaded) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  return <Navigate to={homePathFor(role)} replace />;
}

function App() {
  useTokenRefresh();

  return (
    <BackendHealthCheck>
      <QueryClientProvider client={queryClient}>
        <ConfirmProvider>
          <HashRouter>
            <KeyboardShortcutsProvider>
              <Suspense fallback={
                <div className="flex h-screen w-full items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              }>
                <Routes>
                  <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />

                  <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                    <Route index element={<HomeRedirect />} />
                    <Route path="dashboard" element={adminOnly(<DashboardPage />)} />
                    <Route path="products" element={<RoleRoute access={PERMISSIONS.CATALOG_MANAGE}><ProductsPage /></RoleRoute>} />
                    <Route path="products/new" element={<RoleRoute access={PERMISSIONS.CATALOG_MANAGE}><ProductCreationPage /></RoleRoute>} />
                    <Route path="products/import" element={<RoleRoute access={PERMISSIONS.CATALOG_MANAGE}><ProductImportPage /></RoleRoute>} />
                    <Route path="products/:id/edit" element={<RoleRoute access={PERMISSIONS.CATALOG_MANAGE}><ProductEditPage /></RoleRoute>} />
                    <Route path="products/:id" element={<RoleRoute access={PERMISSIONS.CATALOG_MANAGE}><ProductDetailPage /></RoleRoute>} />
                    <Route path="customers" element={adminOnly(<CustomersPage />)} />
                    <Route path="suppliers" element={adminOnly(<SuppliersPage />)} />
                    <Route path="purchases" element={adminOnly(<PurchasesPage />)} />
                    <Route path="sales" element={<RoleRoute access={PERMISSIONS.SALES_READ_OWN}><SalesPage /></RoleRoute>} />
                    <Route path="pos" element={<RoleRoute access={PERMISSIONS.SALES_CREATE}><POSPage /></RoleRoute>} />
                    <Route path="proformas" element={<RoleRoute access={PERMISSIONS.PROFORMAS_MANAGE}><ProformasPage /></RoleRoute>} />
                    <Route path="expenses" element={adminOnly(<ExpensesPage />)} />
                    <Route path="incomes" element={adminOnly(<IncomesPage />)} />
                    <Route path="cash-register" element={<RoleRoute access={PERMISSIONS.CASH_OPERATE}><CashRegisterPage /></RoleRoute>} />
                    <Route path="customer-accounts" element={adminOnly(<CustomerAccountsPage />)} />
                    <Route path="customer-accounts/:customerId" element={adminOnly(<AccountStatementPage />)} />
                    <Route path="reports" element={adminOnly(<ReportsPage />)} />
                    <Route path="settings" element={adminOnly(<SettingsPage />)} />
                    <Route path="settings/fiscal" element={adminOnly(<FiscalConfigPage />)} />
                    <Route path="settings/users" element={adminOnly(<UsersManagementPage />)} />
                    <Route path="settings/backup" element={adminOnly(<BackupPage />)} />
                    <Route path="settings/branches" element={adminOnly(<BranchesPage />)} />
                    <Route path="categories" element={adminOnly(<CategoriesPage />)} />
                    <Route path="inventory" element={<RoleRoute access={PERMISSIONS.CATALOG_MANAGE}><InventoryPage /></RoleRoute>} />
                  </Route>

                  {/* Cualquier ruta desconocida vuelve a "/", que pasa por el layout (carga el perfil) y HomeRedirect */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </KeyboardShortcutsProvider>
          </HashRouter>

          <Toaster position="top-center" richColors />
          <WindowControls />
        </ConfirmProvider>
      </QueryClientProvider>
    </BackendHealthCheck>
  );
}

export default App;
